import { z } from "zod";
import { Temporal } from "@js-temporal/polyfill";

export const id = z.string().min(1).max(64);
/** Calendar-valid Gregorian dates; no host timezone or Date rollover. */
export const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD").refine((value) => {
  const [year, month, day] = value.split("-").map(Number);
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  return year >= 1 && year <= 9999 && month >= 1 && month <= 12 && day >= 1
    && day <= [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
}, "Use a real calendar date");
export const timeStr = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/, "HH:MM or HH:MM:SS");
/** Local datetime without zone, e.g. 2027-05-15T16:00 (seconds optional). */
export const localDateTime = z.string().refine((value) => {
  const parts = value.split("T");
  return parts.length === 2 && dateStr.safeParse(parts[0]).success && timeStr.safeParse(parts[1]).success;
}, "Use a real local date and time (YYYY-MM-DDTHH:MM)");
/** An absolute timestamp always names its offset; never infer the host zone. */
export const instantDateTime = z.string().refine((value) => {
  const parts = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}(?::\d{2})?)(?:\.\d{1,9})?(Z|[+-]\d{2}:\d{2})$/.exec(value);
  if (!parts || !dateStr.safeParse(parts[1]).success || !timeStr.safeParse(parts[2]).success) return false;
  try { Temporal.Instant.from(value); return true; } catch { return false; }
}, "Use a real ISO timestamp with an explicit timezone offset");
export const cents = z.number().int();
export const nonNegCents = z.number().int().min(0);
/** Percent as decimal text ("3.0", "50"); numbers are accepted and stringified. */
export const pct = z.union([z.string(), z.number()]).transform((v) => String(v).trim()).pipe(z.string().regex(/^-?\d+(\.\d+)?$/, "decimal percent"));
export const nonNegativePct = pct.refine(value => Number.isFinite(Number(value)) && Number(value) >= 0, "Percentage must be non-negative and finite");
export const flag = z.union([z.boolean(), z.literal(0), z.literal(1)]).transform((v) => (v ? 1 : 0));
export const nullableText = z.string().max(4000).nullable();
export const email = z.string().trim().toLowerCase().pipe(z.email());
/** Comma-separated list in a query string → array. */
export const csv = z.string().transform((s) => s.split(",").map((x) => x.trim()).filter(Boolean));
export const limit = z.coerce.number().int().min(1).max(200).default(50);
